// Versão: 1.00
// Data..: 2026-10-03
// Compressão de imagem no navegador antes do upload dos anexos de justificativa.
// JPEG/PNG são redimensionados para no máx. 1600px no maior lado (sem ampliar) e
// re-encodados em JPEG ~0.7 para economizar armazenamento no S3. PDF não é comprimido,
// apenas validado (<= 8MB). Em qualquer falha de suporte/decodificação, devolve o
// arquivo ORIGINAL (com aviso), respeitando o limite de tamanho.

// Limite de tamanho aceito pelo backend (allowlist: image/jpeg, image/png, application/pdf).
const MAX_BYTES = 8 * 1024 * 1024; // 8MB
// Maior dimensão permitida após o redimensionamento.
const MAX_SIDE = 1600;
// Qualidade do re-encode JPEG.
const JPEG_QUALITY = 0.7;

export interface CompressedFile {
  blob: Blob;
  fileName: string;
  contentType: string;
  size: number;
  // true quando não foi possível comprimir e caímos no arquivo original.
  warned?: boolean;
}

// Decodifica o arquivo em um bitmap desenhável. Usa createImageBitmap quando disponível
// (mais rápido e sem anexar ao DOM); senão cai para um <img> via object URL.
async function decode(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    return createImageBitmap(file);
  }
  return await new Promise<HTMLImageElement>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Falha ao decodificar a imagem.')); };
    img.src = url;
  });
}

// Calcula as dimensões de destino respeitando MAX_SIDE (nunca amplia).
function targetSize(width: number, height: number): { w: number; h: number } {
  const largest = Math.max(width, height);
  if (largest <= MAX_SIDE) return { w: width, h: height };
  const scale = MAX_SIDE / largest;
  return { w: Math.round(width * scale), h: Math.round(height * scale) };
}

// Devolve o arquivo original encapsulado (sem comprimir), marcando o aviso.
function original(file: File, warned: boolean): CompressedFile {
  return { blob: file, fileName: file.name, contentType: file.type || 'application/octet-stream', size: file.size, warned };
}

// Comprime (ou valida) um único arquivo. Rejeita se exceder o limite de tamanho
// quando não há como reduzir (PDF ou fallback do original).
export async function compressImage(file: File): Promise<CompressedFile> {
  const type = (file.type || '').toLowerCase();

  // PDF: não comprime, só valida o tamanho.
  if (type === 'application/pdf') {
    if (file.size > MAX_BYTES) {
      throw new Error(`"${file.name}" excede o limite de 8MB.`);
    }
    return { blob: file, fileName: file.name, contentType: 'application/pdf', size: file.size };
  }

  // Fora da allowlist de imagem: devolve original (o backend valida o tipo).
  if (type !== 'image/jpeg' && type !== 'image/png') {
    if (file.size > MAX_BYTES) throw new Error(`"${file.name}" excede o limite de 8MB.`);
    return original(file, true);
  }

  try {
    const source = await decode(file);
    const srcW = (source as ImageBitmap).width ?? (source as HTMLImageElement).naturalWidth;
    const srcH = (source as ImageBitmap).height ?? (source as HTMLImageElement).naturalHeight;
    if (!srcW || !srcH) throw new Error('Dimensões inválidas.');

    const { w, h } = targetSize(srcW, srcH);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas sem contexto 2D.');
    ctx.drawImage(source as CanvasImageSource, 0, 0, w, h);
    // Libera o bitmap decodificado (quando aplicável).
    if (typeof (source as ImageBitmap).close === 'function') (source as ImageBitmap).close();

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
    // toBlob pode retornar null em navegadores sem suporte: cai no original.
    if (!blob) {
      if (file.size > MAX_BYTES) throw new Error(`"${file.name}" excede o limite de 8MB.`);
      return original(file, true);
    }

    const fileName = file.name.replace(/\.(png|jpe?g)$/i, '') + '.jpg';
    const result: CompressedFile = { blob, fileName, contentType: 'image/jpeg', size: blob.size };
    if (result.size > MAX_BYTES) throw new Error(`"${file.name}" ainda excede 8MB após a compressão.`);
    return result;
  } catch (err) {
    // Falha de decodificação/suporte: devolve o original respeitando o limite.
    if (file.size > MAX_BYTES) {
      throw err instanceof Error ? err : new Error('Falha ao processar a imagem.');
    }
    return original(file, true);
  }
}
